/**
 * VCloud Expanded Row Columns
 */
import type { TFunction } from 'i18next'
import type { DataTableColumn } from '@/components/ui/data-table-customized'
import { formatNumberUsage } from '@/utils/function'
import type { VCloudExpandedRow, VCloudColumnVisibility } from './types'
import { parseVMConfig } from './helpers'

// ============================================================================
// Column Visibility Detection
// ============================================================================

export const detectColumnVisibility = (
    rows: VCloudExpandedRow[]
): VCloudColumnVisibility => {
    let hasPublicIP = false
    let hasFloatingIP = false
    let hasBandwidth = false

    rows.forEach((row) => {
        if (
            (row.publicIP && row.publicIP > 0) ||
            (row.totalPublicIP && row.totalPublicIP > 0)
        )
            hasPublicIP = true
        if (
            (row.floatingIP && row.floatingIP > 0) ||
            (row.totalFloatingIP && row.totalFloatingIP > 0)
        )
            hasFloatingIP = true
        if (
            (row.bandwidth && row.bandwidth > 0) ||
            (row.totalBandwidth && row.totalBandwidth > 0)
        )
            hasBandwidth = true
    })

    return { hasPublicIP, hasFloatingIP, hasBandwidth }
}

// ============================================================================
// Column Configuration
// ============================================================================

export const createVCloudExpandedColumns = (
    t: TFunction,
    columnVisibility?: VCloudColumnVisibility
): DataTableColumn<VCloudExpandedRow>[] => {
    const columns: DataTableColumn<VCloudExpandedRow>[] = [
        {
            key: 'service_name',
            title: t('service_name'),
            width: 280,
            render: (_, row) => {
                if (row._rowType === 'service-header') {
                    const isMain = row._isMainService
                    return (
                        <div className="flex items-center gap-1">
                            {!isMain && (
                                <span className="text-xs bg-muted text-muted-foreground px-1.5 py-0.5 rounded mr-1 uppercase">
                                    Addon
                                </span>
                            )}
                            <span className="font-medium">{row.name}</span>
                        </div>
                    )
                }
                if (row._rowType === 'type-header') {
                    return (
                        <span className="pl-4 uppercase text-muted-foreground text-xs">
                            {row.name}
                        </span>
                    )
                }
                if (row._rowType === 'summary') {
                    return <span className="font-medium">{row.name}</span>
                }
                // Item row
                const qty =
                    row.commitment_value && row.commitment_value > 1
                        ? ` (x${row.commitment_value})`
                        : ''
                return (
                    <span className="pl-8">
                        {t(`${row.name}`, { defaultValue: row.name })}
                        {qty}
                    </span>
                )
            },
        },
        {
            key: 'vcores',
            title: 'vCores',
            width: 70,
            cellClassName: 'text-right',
            render: (_, row) => {
                if (
                    row._rowType === 'summary' ||
                    row._rowType === 'service-header'
                ) {
                    return row.totalVCores && row.totalVCores > 0
                        ? formatNumberUsage(row.totalVCores)
                        : null
                }
                if (row._rowType === 'type-header') {
                    if (row._serviceType === 'other_service') return null
                    return row.totalVCores && row.totalVCores > 0
                        ? formatNumberUsage(row.totalVCores)
                        : null
                }
                if (row._serviceType === 'other_service') return null
                const config = parseVMConfig(row.name || '')
                return config.cores > 0 ? formatNumberUsage(config.cores) : null
            },
        },
        {
            key: 'ram',
            title: 'RAM',
            width: 70,
            cellClassName: 'text-right',
            render: (_, row) => {
                if (
                    row._rowType === 'summary' ||
                    row._rowType === 'service-header'
                ) {
                    return row.totalRAM && row.totalRAM > 0
                        ? formatNumberUsage(row.totalRAM)
                        : null
                }
                if (row._rowType === 'type-header') {
                    if (row._serviceType === 'other_service') return null
                    return row.totalRAM && row.totalRAM > 0
                        ? formatNumberUsage(row.totalRAM)
                        : null
                }
                if (row._serviceType === 'other_service') return null
                const config = parseVMConfig(row.name || '')
                return config.ram > 0 ? formatNumberUsage(config.ram) : null
            },
        },
        {
            key: 'storage',
            title: 'Storage',
            width: 80,
            cellClassName: 'text-right',
            render: (_, row) => {
                if (
                    row._rowType === 'summary' ||
                    row._rowType === 'service-header'
                ) {
                    return row.totalStorage && row.totalStorage > 0
                        ? formatNumberUsage(row.totalStorage)
                        : null
                }
                if (row._rowType === 'type-header') {
                    if (row._serviceType !== 'other_service') return null
                    return row.totalStorage && row.totalStorage > 0
                        ? formatNumberUsage(row.totalStorage)
                        : null
                }
                if (row._serviceType !== 'other_service') return null
                return row.storage && row.storage > 0
                    ? formatNumberUsage(row.storage)
                    : null
            },
        },
    ]

    // Public IP column
    if (columnVisibility?.hasPublicIP) {
        columns.push({
            key: 'publicIP',
            title: 'Public IP',
            width: 80,
            cellClassName: 'text-right',
            render: (_, row) => {
                if (
                    row._rowType === 'summary' ||
                    row._rowType === 'service-header'
                ) {
                    return row.totalPublicIP && row.totalPublicIP > 0
                        ? formatNumberUsage(row.totalPublicIP)
                        : null
                }
                if (row._rowType === 'type-header') {
                    if (row._serviceType !== 'other_service') return null
                    return row.totalPublicIP && row.totalPublicIP > 0
                        ? formatNumberUsage(row.totalPublicIP)
                        : null
                }
                if (row._serviceType !== 'other_service') return null
                return row.publicIP && row.publicIP > 0
                    ? formatNumberUsage(row.publicIP)
                    : null
            },
        })
    }

    // Floating IP column
    if (columnVisibility?.hasFloatingIP) {
        columns.push({
            key: 'floatingIP',
            title: 'Floating IP',
            width: 90,
            cellClassName: 'text-right',
            render: (_, row) => {
                if (
                    row._rowType === 'summary' ||
                    row._rowType === 'service-header'
                ) {
                    return row.totalFloatingIP && row.totalFloatingIP > 0
                        ? formatNumberUsage(row.totalFloatingIP)
                        : null
                }
                if (row._rowType === 'type-header') {
                    if (row._serviceType !== 'other_service') return null
                    return row.totalFloatingIP && row.totalFloatingIP > 0
                        ? formatNumberUsage(row.totalFloatingIP)
                        : null
                }
                if (row._serviceType !== 'other_service') return null
                return row.floatingIP && row.floatingIP > 0
                    ? formatNumberUsage(row.floatingIP)
                    : null
            },
        })
    }

    // Bandwidth column
    if (columnVisibility?.hasBandwidth) {
        columns.push({
            key: 'bandwidth',
            title: 'Bandwidth',
            width: 90,
            cellClassName: 'text-right',
            render: (_, row) => {
                if (
                    row._rowType === 'summary' ||
                    row._rowType === 'service-header'
                ) {
                    return row.totalBandwidth && row.totalBandwidth > 0
                        ? formatNumberUsage(row.totalBandwidth)
                        : null
                }
                if (row._rowType === 'type-header') {
                    if (row._serviceType !== 'other_service') return null
                    return row.totalBandwidth && row.totalBandwidth > 0
                        ? formatNumberUsage(row.totalBandwidth)
                        : null
                }
                if (row._serviceType !== 'other_service') return null
                return row.bandwidth && row.bandwidth > 0
                    ? formatNumberUsage(row.bandwidth)
                    : null
            },
        })
    }

    // Price columns
    columns.push(
        {
            key: 'commitment_price_per_unit',
            title: t('unit_price', { defaultValue: 'Đơn giá' }),
            width: 110,
            cellClassName: 'text-right',
            render: (value, row) => {
                if (row.service_pricing_model === 'package') return null
                if (row._rowType !== 'item') return null
                return value
                    ? new Intl.NumberFormat('en-US').format(value)
                    : null
            },
        },
        {
            key: 'commitment_price_total',
            title: t('total_price', { defaultValue: 'Thành tiền' }),
            width: 110,
            cellClassName: 'text-right',
            render: (_, row) => {
                const isPackageRow = row.service_pricing_model === 'package'

                if (isPackageRow) {
                    if (
                        row._rowType !== 'summary' &&
                        row._rowType !== 'service-header'
                    )
                        return null
                    return new Intl.NumberFormat('en-US').format(
                        row.service_price_register ?? 0
                    )
                } else {
                    if (row._rowType === 'type-header') return null
                    if (
                        (row._rowType === 'service-header' ||
                            row._rowType === 'summary') &&
                        row.commitment_price_total
                    ) {
                        return new Intl.NumberFormat('en-US').format(
                            row.commitment_price_total
                        )
                    }
                    return row.commitment_price_total
                        ? new Intl.NumberFormat('en-US').format(
                              row.commitment_price_total
                          )
                        : null
                }
            },
        }
    )

    return columns
}

// ============================================================================
// Row Styling
// ============================================================================

export const getVCloudExpandedRowClassName = (
    row: VCloudExpandedRow
): string => {
    if (row._rowType === 'service-header') {
        return 'bg-muted/40'
    }
    if (row._rowType === 'type-header') {
        return 'bg-muted/20'
    }
    if (row._rowType === 'summary') {
        return 'bg-muted/50 font-medium border-t'
    }
    return ''
}
